import { z } from 'zod/v4';

import { animationSchemaLegacy, animationTypeSchemaLegacy } from '@sites/entity-core';

/**
 * The button's entrance animation, as `create_button_widget` and `update_button_widget` accept it.
 *
 * **Partial, because the entity's group is not.** `animationSchemaLegacy` requires all four keys;
 * a model that wants to switch the animation on would have to restate the type, direction and speed
 * it is not changing, and getting one of them wrong is how a setting the user chose gets quietly
 * reset. The fold merges onto whatever the widget already carries — see `mergeAnimationGroup`.
 *
 * **`running_stripe` is excluded, and that is a deliberate narrowing of the stored enum.** The
 * entity accepts it for every animatable widget, but the editor only OFFERS it for text:
 * `sp-animation-settings-signal-legacy` hides the option behind `showRunningStripe`, which defaults
 * to false and is passed `true` by exactly one control — the text widget's. Left in, it would be a
 * value that validates, persists, and produces a button that animates in no visible way. Derived
 * with `.exclude()` rather than retyped so a fifth type added to the host enum lands here on its
 * own.
 *
 * **`animationPosition` has no `center` here**, unlike the column's. The two vocabularies are
 * genuinely different objects: a widget stores `animationSchemaLegacy` (four directions,
 * `running_stripe`), a column stores the versioned `features/animation` (three types, five
 * directions including `center`). Neither is a superset of the other, which is why this schema and
 * the column's are declared apart instead of shared.
 */
export const buttonAnimationSchema = animationSchemaLegacy
    .partial()
    .extend({
        animationType: animationTypeSchemaLegacy
            .exclude(['running_stripe'])
            .optional()
            .describe(
                'What the button group does as it arrives: "fade" fades in, "scale" grows from nothing, ' +
                    '"slide" only travels — pair "slide" with an animationPosition, or it moves nowhere and ' +
                    'nothing is visible.'
            ),
        animationPosition: z
            .enum(['left', 'top', 'right', 'bottom'])
            .optional()
            .describe(
                'Where it travels FROM — "left"/"right" shift it 100px sideways, "top"/"bottom" 100px ' +
                    'vertically. Read for every type, not just "slide": "fade" + "top" is a fade that also ' +
                    'drifts down into place.'
            ),
        animationSpeed: z
            .enum(['slow', 'medium', 'fast'])
            .optional()
            .describe('How long it takes: "slow" 0.8s, "medium" 0.4s, "fast" 0.2s.'),
        isActive: z
            .boolean()
            .optional()
            .describe('Play the animation. The other three are not read while this is false.'),
    })
    .describe(
        'Entrance animation for the button GROUP, played when it scrolls into view — one animation for the ' +
            'whole widget, not per button. Partial: only the keys you provide change, so { "isActive": true } ' +
            'turns on whatever is already configured.'
    );
